import { parseNumberCell, sampleSpecColumnKeys, type SampleSpecRow } from '../sample-sheet';
import { evaluateFieldSoftConstraint } from '../sample-sheet/constraints';
import { hasAcceptedRange, sampleSheetColumnMetadata } from '../sample-sheet/metadata';
import type { RowValidationIssues, SampleSheetDraftRow } from './types';

function hasSoftConstraint(fieldKey: keyof SampleSpecRow): boolean {
  if (!hasAcceptedRange(fieldKey)) {
    return false;
  }
  return sampleSheetColumnMetadata[fieldKey].softValidationEnabled !== false;
}

const fieldsWithAcceptedRanges = sampleSpecColumnKeys.filter(
  (key): key is keyof SampleSpecRow => hasSoftConstraint(key as keyof SampleSpecRow),
);

export function evaluateSoftConstraints(row: SampleSheetDraftRow): RowValidationIssues {
  const warnings: RowValidationIssues = {};

  fieldsWithAcceptedRanges.forEach(fieldKey => {
    const rawValue = Reflect.get(row, fieldKey);
    const numericValue = parseNumberCell(rawValue);
    if (typeof numericValue !== 'number') {
      return;
    }
    const fieldWarnings = evaluateFieldSoftConstraint(fieldKey, numericValue);
    if (fieldWarnings.length > 0) {
      warnings[fieldKey] = fieldWarnings;
    }
  });

  return warnings;
}

export function normalizeSoftWarnings(value: unknown): RowValidationIssues | null {
  if (!value || typeof value !== 'object') {
    return null;
  }
  const result: RowValidationIssues = {};
  Object.entries(value as Record<string, unknown>).forEach(([field, maybeMessages]) => {
    if (!fieldsWithAcceptedRanges.includes(field as keyof SampleSpecRow)) {
      return;
    }
    if (!Array.isArray(maybeMessages)) {
      return;
    }
    const messages = maybeMessages.filter((message): message is string => typeof message === 'string' && message.length > 0);
    if (messages.length > 0) {
      result[field as keyof SampleSheetDraftRow] = messages;
    }
  });
  return Object.keys(result).length > 0 ? result : null;
}
